import { useState, useEffect, useMemo, useRef } from 'react';
import { ref, get, update } from 'firebase/database';
import { HiOutlineStar, HiStar, HiOutlineTrash } from 'react-icons/hi';
import { db } from '../firebase';
import { WATCHLIST_REF, WATCHLIST_TIMESTAMP_KEYS } from '../admin/adminConstants';
import ScrollToTop from '../components/ScrollToTop';

const ANALYSIS_REF = 'TSS-Analysis';
const ANALYZE_WATCHLISTS_REF = 'TSS-Config/analyze_watchlists';
const WATCHLIST_DISABLED = 'DISABLED';

function getAnalysisRefreshMinutesRef(uid) {
  return uid ? `Users/${uid}/settings/analysisRefreshMinutes` : null;
}

function getFavouritesRef(uid) {
  return uid ? `Users/${uid}/favourites` : null;
}

function timestampToMs(timestamp) {
  if (timestamp == null || timestamp === '') return null;
  let ms = null;
  if (typeof timestamp === 'number') {
    ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  } else if (typeof timestamp === 'string') {
    const s = timestamp.trim().replace(/\+00:00Z$/i, 'Z');
    const parsed = Date.parse(s);
    ms = Number.isNaN(parsed) ? Number(timestamp) : parsed;
    if (typeof ms === 'number' && !Number.isNaN(ms) && ms < 1e12 && ms > 0) ms = ms * 1000;
  } else if (typeof timestamp === 'object' && timestamp !== null) {
    const sec = timestamp.seconds ?? timestamp._seconds ?? timestamp.sec;
    if (typeof sec === 'number') ms = sec * 1000;
  }
  return ms != null && !Number.isNaN(ms) && ms > 0 ? ms : null;
}

function formatLastAnalyzed(timestamp) {
  const ms = timestampToMs(timestamp);
  if (ms == null) return '—';
  const now = Date.now();
  const diffMs = now - ms;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  const diffWeek = Math.floor(diffDay / 7);

  if (diffMs < 0) {
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  if (diffSec < 60) return diffSec <= 1 ? 'just now' : `${diffSec} seconds ago`;
  if (diffMin < 60) return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
  if (diffHr < 24) return diffHr === 1 ? '1 hour ago' : `${diffHr} hours ago`;
  if (diffDay < 2) return 'yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  if (diffWeek < 4) return diffWeek === 1 ? '1 week ago' : `${diffWeek} weeks ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatVolume(num) {
  if (num == null || num === '') return '—';
  const n = Number(num);
  if (Number.isNaN(n)) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
}

function healthClass(health) {
  if (!health || typeof health !== 'string') return '';
  const h = health.toLowerCase();
  if (h === 'good') return 'analysis-health-good';
  if (h === 'bad') return 'analysis-health-bad';
  return 'analysis-health-neutral';
}

const ANALYSIS_COLUMNS = [
  { key: 'Ticker', label: 'Ticker' },
  { key: 'CompanyName', label: 'Company' },
  { key: 'Score', label: 'Tradeviser Score' },
  { key: 'Signal', label: 'Tradeviser Signal' },
  { key: 'Price', label: 'Price' },
  { key: 'PERatio', label: 'P/E' },
  { key: 'EPS', label: 'EPS' },
  { key: 'RSI', label: 'RSI' },
  { key: 'SMA50', label: 'SMA50' },
  { key: 'SMA200', label: 'SMA200' },
  { key: 'SupportLevel', label: 'Support' },
  { key: 'ResistanceLevel', label: 'Resistance' },
  { key: 'AnalystRating', label: 'Analyst' },
  { key: 'Trend', label: 'Trend' },
  { key: 'MarketSentiment', label: 'Sentiment' },
  { key: 'TradingVolume', label: 'Volume' },
  { key: 'timestamp', label: 'Last analyzed' },
];

function getSortValue(row, key) {
  const v = key === 'Ticker' ? (row.Ticker ?? row.ticker) : row[key];
  if (v == null || v === '') return null;
  if (key === 'timestamp') return timestampToMs(v);
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  return String(v).toLowerCase();
}

function Analysis({ user, isAdmin }) {
  const [analysisByGroup, setAnalysisByGroup] = useState(null);
  const [analyzeWatchlists, setAnalyzeWatchlists] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState('snp500');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [favouritesByGroup, setFavouritesByGroup] = useState({});
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
  const [togglingFavourite, setTogglingFavourite] = useState(null);
  const [deletingAnalysis, setDeletingAnalysis] = useState(null);

  const defaultGroupApplied = useRef(false);
  const [refreshIntervalMinutes, setRefreshIntervalMinutes] = useState(0);

  const loadAnalysisSilent = useRef(async () => {
    try {
      const snapshot = await get(ref(db, ANALYSIS_REF));
      const data = snapshot.val();
      const byGroup = typeof data === 'object' && data !== null ? data : {};
      setAnalysisByGroup(byGroup);
    } catch {
      // Keep existing data on background refresh failure
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const [analysisSnap, watchlistsSnap] = await Promise.all([
          get(ref(db, ANALYSIS_REF)),
          get(ref(db, ANALYZE_WATCHLISTS_REF)),
        ]);
        if (cancelled) return;
        const byGroup = typeof analysisSnap.val() === 'object' && analysisSnap.val() !== null ? analysisSnap.val() : {};
        const watchlists = typeof watchlistsSnap.val() === 'object' && watchlistsSnap.val() !== null ? watchlistsSnap.val() : {};
        setAnalysisByGroup(byGroup);
        setAnalyzeWatchlists(watchlists);
        if (!defaultGroupApplied.current) {
          defaultGroupApplied.current = true;
          const allKeys = Object.keys(byGroup).sort((a, b) => a.localeCompare(b));
          const enabledKeys = allKeys.filter((c) => watchlists[c] != null && watchlists[c] !== '' && watchlists[c] !== WATCHLIST_DISABLED && typeof watchlists[c] === 'string');
          const snp500Key = enabledKeys.find((k) => k === 'snp500');
          if (snp500Key) {
            setSelectedGroup(snp500Key);
          } else if (enabledKeys.length > 0) {
            setSelectedGroup(enabledKeys[0]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load analysis');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      setRefreshIntervalMinutes(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const path = getAnalysisRefreshMinutesRef(uid);
        if (!path) return;
        const snapshot = await get(ref(db, path));
        const value = snapshot.val();
        if (!cancelled) {
          const n = typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
          setRefreshIntervalMinutes(n);
        }
      } catch {
        if (!cancelled) setRefreshIntervalMinutes(0);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  useEffect(() => {
    if (refreshIntervalMinutes <= 0) return;
    const ms = refreshIntervalMinutes * 60 * 1000;
    const id = setInterval(() => {
      loadAnalysisSilent.current();
    }, ms);
    return () => clearInterval(id);
  }, [refreshIntervalMinutes]);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      setFavouritesByGroup({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const favRef = getFavouritesRef(uid);
        if (!favRef) return;
        const snapshot = await get(ref(db, favRef));
        const data = snapshot.val();
        if (!cancelled) {
          setFavouritesByGroup(typeof data === 'object' && data !== null ? data : {});
        }
      } catch {
        if (!cancelled) setFavouritesByGroup({});
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const watchlistCodes = useMemo(() => {
    if (!analysisByGroup || !analyzeWatchlists) return [];
    const allKeys = Object.keys(analysisByGroup).sort((a, b) => a.localeCompare(b));
    const enabledKeys = allKeys.filter((c) => analyzeWatchlists[c] != null && analyzeWatchlists[c] !== '' && analyzeWatchlists[c] !== WATCHLIST_DISABLED && typeof analyzeWatchlists[c] === 'string');
    const snp500Key = enabledKeys.find((k) => k === 'snp500');
    return snp500Key ? [snp500Key, ...enabledKeys.filter((c) => c !== snp500Key)] : enabledKeys;
  }, [analysisByGroup, analyzeWatchlists]);

  const allStocks = useMemo(() => {
    if (!selectedGroup || !analysisByGroup?.[selectedGroup]) return [];
    const raw = analysisByGroup[selectedGroup];
    return Object.entries(raw).map(([tickerKey, obj]) => ({
      ...obj,
      ticker: obj.Ticker ?? tickerKey,
    }));
  }, [analysisByGroup, selectedGroup]);

  const filteredByFavourites = useMemo(() => {
    if (!showFavouritesOnly || !selectedGroup) return allStocks;
    const favs = favouritesByGroup[selectedGroup];
    if (!favs || typeof favs !== 'object') return [];
    return allStocks.filter((s) => favs[s.ticker] === true);
  }, [allStocks, showFavouritesOnly, selectedGroup, favouritesByGroup]);

  const query = searchQuery.trim().toLowerCase();
  const stocks = useMemo(() => {
    if (!query) return filteredByFavourites;
    return filteredByFavourites.filter(
      (s) =>
        (s.ticker && String(s.ticker).toLowerCase().includes(query)) ||
        (s.CompanyName && s.CompanyName.toLowerCase().includes(query)) ||
        (s.Signal && s.Signal.toLowerCase().includes(query)) ||
        (s.Trend && s.Trend.toLowerCase().includes(query)) ||
        (s.AnalystRating && s.AnalystRating.toLowerCase().includes(query)) ||
        (s.MarketSentiment && s.MarketSentiment.toLowerCase().includes(query))
    );
  }, [filteredByFavourites, query]);

  const sortedStocks = useMemo(() => {
    if (!sortKey || stocks.length === 0) return stocks;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...stocks].sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      const na = va == null;
      const nb = vb == null;
      if (na && nb) return 0;
      if (na) return 1;
      if (nb) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb));
    });
  }, [stocks, sortKey, sortDir]);

  const handleSort = (key) => {
    setSortKey(key);
    setSortDir((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
  };

  const handleToggleFavourite = async (group, ticker) => {
    const uid = user?.uid;
    if (!uid) return;
    setTogglingFavourite(ticker);
    const groupFavs = favouritesByGroup[group] || {};
    const isFav = groupFavs[ticker] === true;
    const nextFav = !isFav;
    const baseRef = getFavouritesRef(uid);
    if (!baseRef) return;
    try {
      await update(ref(db, `${baseRef}/${group}`), { [ticker]: nextFav ? true : null });
      setFavouritesByGroup((prev) => {
        const next = { ...prev };
        next[group] = { ...(next[group] || {}) };
        if (nextFav) {
          next[group][ticker] = true;
        } else {
          delete next[group][ticker];
        }
        return next;
      });
    } finally {
      setTogglingFavourite(null);
    }
  };

  const handleDeleteAnalysis = async (group, ticker) => {
    if (!group || !ticker) return;
    if (!window.confirm(`Delete analysis for ${ticker} in ${group}? This cannot be undone.`)) return;
    setDeletingAnalysis(ticker);
    try {
      await Promise.all([
        update(ref(db, `${ANALYSIS_REF}/${group}`), { [ticker]: null }),
        update(
          ref(db, `${WATCHLIST_REF}/${group}/${ticker}`),
          WATCHLIST_TIMESTAMP_KEYS.reduce((acc, key) => {
            acc[key] = null;
            return acc;
          }, {})
        ),
      ]);
      setAnalysisByGroup((prev) => {
        if (!prev?.[group]) return prev;
        const next = { ...prev };
        next[group] = { ...next[group] };
        delete next[group][ticker];
        return next;
      });
    } finally {
      setDeletingAnalysis(null);
    }
  };

  if (loading) {
    return (
      <div className="page page--full-width analysis-page-wrap">
        <div className="analysis-page">
          <header className="analysis-header">
            <h1 className="analysis-title">Analysis</h1>
          </header>
          <p className="analysis-placeholder">Loading…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page page--full-width analysis-page-wrap">
        <div className="analysis-page">
          <header className="analysis-header">
            <h1 className="analysis-title">Analysis</h1>
          </header>
          <p className="analysis-placeholder analysis-placeholder--error" role="alert">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--full-width analysis-page-wrap">
      <div className="analysis-page">
        <header className="analysis-header">
          <h1 className="analysis-title">Analysis</h1>
        </header>

        {watchlistCodes.length === 0 ? (
          <p className="analysis-placeholder">No analysis data yet.</p>
        ) : (
        <div className="analysis-content">
          <nav className="analysis-tabs" role="tablist" aria-label="Watchlist">
            {watchlistCodes.map((code) => (
              <button
                key={code}
                type="button"
                role="tab"
                aria-selected={selectedGroup === code}
                aria-controls={`analysis-panel-${code}`}
                id={`analysis-tab-${code}`}
                className={`analysis-tab ${selectedGroup === code ? 'analysis-tab--active' : ''}`}
                onClick={() => setSelectedGroup(code)}
              >
                {code}
              </button>
            ))}
          </nav>

          {watchlistCodes.map((code) => (
            <div
              key={code}
              id={`analysis-panel-${code}`}
              role="tabpanel"
              aria-labelledby={`analysis-tab-${code}`}
              hidden={selectedGroup !== code}
              className="analysis-panel"
            >
              {selectedGroup === code && (
                <>
                  <div className="analysis-toolbar">
                    <div className="analysis-search-wrap">
                      <input
                        type="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by ticker, name, signal, trend…"
                        className="analysis-search"
                        aria-label="Filter stocks"
                      />
                    </div>
                    <label className="analysis-favourites-filter">
                      <input
                        type="checkbox"
                        checked={showFavouritesOnly}
                        onChange={(e) => setShowFavouritesOnly(e.target.checked)}
                        aria-label="Show favourites only"
                      />
                      <span>Favourites only</span>
                    </label>
                  </div>
                  {stocks.length === 0 ? (
                    <div className="analysis-empty">
                      <p className="analysis-placeholder">
                        {filteredByFavourites.length === 0 && showFavouritesOnly
                          ? 'No favourites in this watchlist. Star items to add them.'
                          : allStocks.length === 0
                            ? 'No analysis in this watchlist.'
                            : 'No stocks match your search.'}
                      </p>
                    </div>
                  ) : (
                    <div className="analysis-table-wrap">
                      <table className="analysis-table">
                        <thead>
                          <tr>
                            <th className="analysis-th-fav" aria-label="Favourite">
                              <HiOutlineStar className="analysis-fav-header-icon" aria-hidden />
                            </th>
                            {ANALYSIS_COLUMNS.map(({ key, label }) => (
                              <th key={key}>
                                <button
                                  type="button"
                                  className="analysis-th-sort"
                                  onClick={() => handleSort(key)}
                                  aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                                >
                                  {label}
                                  {sortKey === key && (
                                    <span className="analysis-sort-icon" aria-hidden>
                                      {sortDir === 'asc' ? ' ↑' : ' ↓'}
                                    </span>
                                  )}
                                </button>
                              </th>
                            ))}
                            {isAdmin && <th className="analysis-th-actions">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedStocks.map((s) => {
                            const isFav = favouritesByGroup[selectedGroup]?.[s.ticker] === true;
                            return (
                            <tr key={s.ticker}>
                              <td className="analysis-cell-fav">
                                <button
                                  type="button"
                                  className="analysis-fav-btn"
                                  onClick={() => handleToggleFavourite(selectedGroup, s.ticker)}
                                  disabled={togglingFavourite === s.ticker}
                                  aria-label={isFav ? `Remove ${s.ticker} from favourites` : `Add ${s.ticker} to favourites`}
                                  title={isFav ? 'Remove from favourites' : 'Add to favourites'}
                                >
                                  {isFav ? (
                                    <HiStar className="analysis-fav-icon analysis-fav-icon--on" aria-hidden />
                                  ) : (
                                    <HiOutlineStar className="analysis-fav-icon" aria-hidden />
                                  )}
                                </button>
                              </td>
                              <td className="allstocks-cell-ticker">{s.Ticker ?? s.ticker}</td>
                              <td>{s.CompanyName ?? '—'}</td>
                              <td className="allstocks-cell-num analysis-cell-accent">{s.Score ?? '—'}</td>
                              <td className="analysis-cell-accent">{s.Signal ?? '—'}</td>
                              <td className="allstocks-cell-num">{s.Price != null ? (typeof s.Price === 'number' ? s.Price.toFixed(2) : s.Price) : '—'}</td>
                              <td className={`allstocks-cell-num ${healthClass(s.PERatioHealth)}`}>{s.PERatio != null ? (typeof s.PERatio === 'number' ? s.PERatio.toFixed(2) : s.PERatio) : '—'}</td>
                              <td className={`allstocks-cell-num ${healthClass(s.EPSHealth)}`}>{s.EPS != null ? (typeof s.EPS === 'number' ? s.EPS.toFixed(2) : s.EPS) : '—'}</td>
                              <td className={`allstocks-cell-num ${healthClass(s.RSIHealth)}`}>{s.RSI != null ? (typeof s.RSI === 'number' ? s.RSI.toFixed(2) : s.RSI) : '—'}</td>
                              <td className={`allstocks-cell-num ${healthClass(s.SMA50Health)}`}>{s.SMA50 != null ? (typeof s.SMA50 === 'number' ? s.SMA50.toFixed(2) : s.SMA50) : '—'}</td>
                              <td className={`allstocks-cell-num ${healthClass(s.SMA200Health)}`}>{s.SMA200 != null ? (typeof s.SMA200 === 'number' ? s.SMA200.toFixed(2) : s.SMA200) : '—'}</td>
                              <td className={`allstocks-cell-num ${healthClass(s.SupportHealth)}`}>{s.SupportLevel != null ? (typeof s.SupportLevel === 'number' ? s.SupportLevel.toFixed(2) : s.SupportLevel) : '—'}</td>
                              <td className={`allstocks-cell-num ${healthClass(s.ResistanceHealth)}`}>{s.ResistanceLevel != null ? (typeof s.ResistanceLevel === 'number' ? s.ResistanceLevel.toFixed(2) : s.ResistanceLevel) : '—'}</td>
                              <td className={healthClass(s.AnalystHealth)}>{s.AnalystRating ?? '—'}</td>
                              <td className={healthClass(s.TrendHealth)}>{s.Trend ?? '—'}</td>
                              <td className={healthClass(s.SentimentHealth)}>{s.MarketSentiment ?? '—'}</td>
                              <td className={`allstocks-cell-num ${healthClass(s.TradingVolumeHealth)}`}>{formatVolume(s.TradingVolume)}</td>
                              <td className="allstocks-cell-muted">{formatLastAnalyzed(s.timestamp)}</td>
                              {isAdmin && (
                                <td className="analysis-cell-actions">
                                  <button
                                    type="button"
                                    className="analysis-delete-btn"
                                    onClick={() => handleDeleteAnalysis(selectedGroup, s.ticker)}
                                    disabled={deletingAnalysis === s.ticker}
                                    title={`Delete analysis for ${s.ticker}`}
                                  >
                                    <HiOutlineTrash aria-hidden className="analysis-delete-icon" />
                                    <span>Delete</span>
                                  </button>
                                </td>
                              )}
                            </tr>
                          ); })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        )}
      </div>
      <ScrollToTop />
    </div>
  );
}

export default Analysis;
